"use client";

import { useEffect, useState } from "react";
import { Mail, ArrowRight, Check, Loader2 } from "lucide-react";
import { getRecaptchaToken } from "@/lib/recaptcha";
import {
  fetchPublicForm,
  submitForm,
  type MarketingFormOptIn,
} from "@/lib/api/forms";
import {
  prefillContactInfoValues,
  saveContactInfoFromSubmittedValues,
} from "@/lib/contact-prefill";
import { RevealOnScroll } from "@/components/reveal-on-scroll";
import { MarketingOptIns } from "@/components/forms/built-in-marketing-form";
import { HeardAboutUsSelect, useHeardAboutUsOptions } from "@/components/forms/heard-about-us-select";

// `heading` is h1 when the signup is the whole page, as on /newsletter.
export function NewsletterSection({ title = "Stay in the Current", description = "Get the latest electric boating news, new inventory alerts, and exclusive event invitations delivered monthly.", heading: Heading = "h2" }: { title?: string; description?: string; heading?: "h1" | "h2" } = {}) {
  const [email, setEmail] = useState("");
  const [placeholder, setPlaceholder] = useState("Enter your email");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptIns, setLoadingOptIns] = useState(true);
  const [marketingOptIns, setMarketingOptIns] = useState<MarketingFormOptIn[]>([]);
  const [selectedMarketingOptIns, setSelectedMarketingOptIns] = useState<number[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const heardAboutUsOptions = useHeardAboutUsOptions();
  const [selfReportedSource, setSelfReportedSource] = useState("");

  useEffect(() => {
    let active = true;
    setEmail((current) => prefillContactInfoValues({ email: current }).email);
    fetchPublicForm("newsletter")
      .then((form) => {
        if (!active) return;
        const emailField = form?.fields.find((field) => field.field_key === "email");
        if (emailField?.placeholder || emailField?.label) {
          setPlaceholder(emailField.placeholder || emailField.label);
        }
        setMarketingOptIns(form?.marketing_opt_ins ?? []);
        setLoadingOptIns(false);
      })
      .catch(() => {
        if (active) setLoadingOptIns(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");

    // Honeypot check
    const hp = (e.target as HTMLFormElement).querySelector<HTMLInputElement>('[name="website"]');
    if (hp?.value) { setSubmitted(true); return; }

    try {
      const recaptcha_token = await getRecaptchaToken("submit_newsletter");
      const result = await submitForm("newsletter", {
        email,
        marketing_opt_ins: selectedMarketingOptIns,
        source_url: window.location.href,
        recaptcha_token,
      }, { selfReportedSource });
      if (!result.success) {
        setErrorMsg(result.message);
        return;
      }
      saveContactInfoFromSubmittedValues({ email });
      setSubmitted(true);
      setEmail("");
      setSelectedMarketingOptIns([]);
      setSelfReportedSource("");
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      setErrorMsg("Unable to subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const missingRequiredMarketingOptIn = marketingOptIns.some(
    (optIn) => optIn.required && !selectedMarketingOptIns.includes(optIn.topic_id),
  );

  return (
    <section className="relative py-20 bg-background border-t border-border">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
        <RevealOnScroll className="space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-em-blue/10 text-em-blue">
            <Mail className="h-6 w-6" />
          </div>
          <Heading
            className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {title}
          </Heading>
          <p className="text-muted-foreground">
            {description}
          </p>

          <form
            onSubmit={handleSubmit}
            className="mx-auto max-w-md space-y-3 pt-4"
          >
            <input type="text" name="website" autoComplete="off" className="hidden" tabIndex={-1} aria-hidden="true" />
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder}
                autoComplete="email"
                className="flex-1 px-5 py-3 rounded-full bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50 focus:border-em-blue transition-all"
              />
              <button
                type="submit"
                disabled={submitted || submitting || loadingOptIns || missingRequiredMarketingOptIn}
                className="em-button em-button--primary shrink-0"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : submitted ? (
                  <>
                    <Check className="h-4 w-4" />
                    Subscribed!
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
            {heardAboutUsOptions.length > 0 && (
              <HeardAboutUsSelect
                options={heardAboutUsOptions}
                value={selfReportedSource}
                className="w-full px-5 py-3 rounded-full bg-card border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50 focus:border-em-blue transition-all"
                onChange={setSelfReportedSource}
              />
            )}
            <MarketingOptIns
              optIns={marketingOptIns}
              selectedTopicIds={selectedMarketingOptIns}
              className="text-left"
              onChange={setSelectedMarketingOptIns}
            />
            {errorMsg && <p className="text-sm text-red-500">{errorMsg}</p>}
          </form>

          <p className="text-xs text-muted-foreground/60 pt-2">
            No spam. Unsubscribe anytime. We respect your inbox.
          </p>
        </RevealOnScroll>
      </div>
    </section>
  );
}
